import { useState, useEffect, useCallback } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Bell, BellOff, Check, Loader2, Radio, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';
import { API } from '@/config/api';

// VAPID public key
const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, c => c.charCodeAt(0));
}

// Get current app channel from PWA context
function getAppChannel() {
  return window.__ASTERPOS_PWA__?.app || null;
}

const CHANNEL_COLORS = {
  pos: '#1e40af', kds: '#6366f1', kiosk: '#7c3aed', admin: '#059669',
  admins: '#4f46e5', order: '#f97316', expo: '#0d9488', display: '#d97706',
  timeclock: '#10b981', staff: '#059669',
};

export default function PushNotificationManager() {
  const { token, isAuthenticated } = useAuthStore();
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [channels, setChannels] = useState(null);
  const [subscriptions, setSubs] = useState([]);

  const [preferences, setPreferences] = useState({
    orderUpdates: true,
    promotions: false,
    reminders: true,
  });

  const appChannel = getAppChannel();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const checkSubscription = async (reg) => {
    const sub = await reg.pushManager.getSubscription();
    setIsSubscribed(!!sub);
  };

  const loadChannels = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/channels`);
      setChannels(data.channels);
    } catch { /* channels endpoint optional */ }
  };

  const loadSubscriptions = useLatestCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/subscriptions`, { headers });
      setSubs(data.subscriptions || []);
    } catch { /* ok */ }
  });

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(reg => {
        setRegistration(reg);
        checkSubscription(reg);
      });
    }
    loadChannels();
    if (isAuthenticated) loadSubscriptions();
  }, [isAuthenticated, loadSubscriptions]);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) { toast.error('Push notifications not supported'); return false; }
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') { toast.success('Notifications enabled!'); return true; }
    if (result === 'denied') { toast.error('Notifications blocked. Enable in browser settings.'); }
    return false;
  }, []);

  const subscribe = async () => {
    if (!registration) { toast.error('Service worker not ready'); return; }
    setLoading(true);
    try {
      if (permission !== 'granted') {
        const granted = await requestPermission();
        if (!granted) return;
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      if (isAuthenticated) {
        await axios.post(`${API}/notifications/subscribe`, {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth')))),
          },
          app_channel: appChannel,
        }, { headers });
      }
      setIsSubscribed(true);
      loadSubscriptions();
      const channelLabel = channels?.[appChannel]?.name || 'this app';
      toast.success(`Subscribed to ${channelLabel} notifications`);
      if (navigator.vibrate) navigator.vibrate(100);
    } catch (err) {
      console.error('Push subscription failed:', err);
      toast.error('Failed to enable notifications');
    } finally { setLoading(false); }
  };

  const unsubscribe = async () => {
    if (!registration) return;
    setLoading(true);
    try {
      const sub = await registration.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        if (isAuthenticated) {
          await axios.delete(`${API}/notifications/subscribe`, {
            data: { endpoint: sub.endpoint, keys: {}, app_channel: appChannel },
            headers,
          });
        }
      }
      setIsSubscribed(false);
      loadSubscriptions();
      toast.success('Notifications disabled');
    } catch (err) {
      console.error('Unsubscribe failed:', err);
      toast.error('Failed to disable notifications');
    } finally { setLoading(false); }
  };

  const updatePreferences = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    if (isAuthenticated) {
      try {
        await axios.put(`${API}/notifications/preferences`, newPrefs, { headers });
        toast.success('Preferences saved');
      } catch { /* ok */ }
    }
  };

  const testNotification = async () => {
    if (permission !== 'granted') { toast.error('Enable notifications first'); return; }
    // Server-side test with channel targeting
    if (isAuthenticated) {
      try {
        await axios.post(`${API}/notifications/test`, { channel: appChannel }, { headers });
        toast.success('Test notification sent');
      } catch { /* fallback to local */ }
    }
    // Local notification fallback
    const iconPath = appChannel ? `/pwa/icons/${appChannel}-192.svg` : '/icons/icon-192x192.svg';
    const channelName = channels?.[appChannel]?.name || 'Chain29';
    new Notification(`${channelName} - Test`, {
      body: `Push notifications are working for ${channelName}!`,
      icon: iconPath,
      badge: iconPath,
      vibrate: [200, 100, 200],
      tag: `test-${appChannel || 'general'}`,
      data: { channel: appChannel },
    });
  };

  const isSupported = typeof Notification !== 'undefined' && 'serviceWorker' in navigator;
  if (!isSupported) {
    return (
      <Card data-testid="push-notif-unsupported">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BellOff className="h-5 w-5" /> Notifications Not Supported</CardTitle>
          <CardDescription>Your browser doesn&apos;t support push notifications.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const currentChannel = channels?.[appChannel];
  const accentColor = CHANNEL_COLORS[appChannel] || '#f97316';

  return (
    <Card data-testid="push-notif-manager">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" /> Push Notifications
          {currentChannel && (
            <span className="ml-auto text-xs font-normal px-2 py-0.5 rounded-full" style={{ background: accentColor + '20', color: accentColor }}
              data-testid="push-notif-channel-badge">
              <Radio className="w-3 h-3 inline mr-1" />{currentChannel.name}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          {currentChannel
            ? currentChannel.description
            : 'Get real-time updates about your orders'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Enable Notifications</p>
            <p className="text-sm text-muted-foreground">
              {permission === 'denied'
                ? 'Blocked in browser settings'
                : isSubscribed
                  ? `Receiving ${currentChannel?.name || 'all'} notifications`
                  : `Enable to get ${currentChannel?.name || 'app'} updates`}
            </p>
          </div>
          <Button variant={isSubscribed ? 'outline' : 'default'} size="sm"
            onClick={isSubscribed ? unsubscribe : subscribe}
            disabled={loading || permission === 'denied'}
            data-testid="push-notif-toggle">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> :
              isSubscribed ? <><Check className="h-4 w-4 mr-1" />Enabled</> : 'Enable'}
          </Button>
        </div>

        {/* Channel Topics */}
        {isSubscribed && currentChannel && (
          <div className="space-y-2 pt-3 border-t">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <Zap className="w-3 h-3 inline mr-1" />Notification Topics
            </p>
            <div className="flex flex-wrap gap-1.5">
              {currentChannel.topics.map(topic => (
                <span key={topic} className="text-xs px-2 py-1 rounded-md bg-muted text-muted-foreground"
                  data-testid={`push-topic-${topic}`}>
                  {topic.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Preferences */}
        {isSubscribed && (
          <div className="space-y-4 pt-3 border-t">
            <p className="text-sm font-medium">Notification Types</p>
            {[
              { key: 'orderUpdates', label: 'Order Updates', desc: 'Status changes, delivery alerts' },
              { key: 'promotions', label: 'Promotions', desc: 'Deals, discounts, offers' },
              { key: 'reminders', label: 'Reminders', desc: 'Reorder suggestions, shift reminders' },
            ].map(p => (
              <div key={p.key} className="flex items-center justify-between">
                <div>
                  <p className="text-sm">{p.label}</p>
                  <p className="text-xs text-muted-foreground">{p.desc}</p>
                </div>
                <Switch checked={preferences[p.key]} onCheckedChange={v => updatePreferences(p.key, v)}
                  data-testid={`push-pref-${p.key}`} />
              </div>
            ))}
          </div>
        )}

        {/* Active Subscriptions Summary */}
        {subscriptions.length > 0 && (
          <div className="pt-3 border-t">
            <p className="text-xs text-muted-foreground mb-2">Active on {subscriptions.length} device{subscriptions.length > 1 ? 's' : ''}</p>
            <div className="flex flex-wrap gap-1">
              {subscriptions.map((s, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                  data-testid={`push-sub-${i}`}>
                  {s.app_channel || 'general'}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Test Button */}
        {isSubscribed && (
          <Button variant="outline" size="sm" onClick={testNotification} className="w-full"
            data-testid="push-notif-test">
            Send Test Notification
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
